/* Sōk cart: the cart page and cart drawer share one controller built on Shopify's Ajax Cart API and
   Section Rendering API. Sauna bundles change as a group (remove, swap installation, add an extra),
   which Dawn's one-line-at-a-time cart components don't cover. */

if (!window.SokCart) {
  const UNDO_KEY = 'sok-cart-undo';

  // Liquid's `line.properties | json` gives [[key, value], ...]; the Ajax API wants an object.
  const toProps = (p) => (Array.isArray(p) ? Object.fromEntries(p) : p || {});
  const normalise = (items) => items.map((item) => ({ ...item, properties: toProps(item.properties) }));

  const SokCart = {
    roots() {
      return [...document.querySelectorAll('sok-cart, cart-drawer.sok-drawer')];
    },

    sections() {
      const ids = new Set(['cart-icon-bubble']);
      this.roots().forEach((root) => ids.add(root.dataset.section));
      return [...ids];
    },

    async post(path, body) {
      const response = await fetch(`${path}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sections(), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart error');
      return data;
    },

    async fetchSections() {
      const response = await fetch(`${window.location.pathname}?sections=${this.sections().join(',')}&t=${Date.now()}`);
      return response.json();
    },

    // Section Rendering can occasionally return null straight after a cart change. Retry once, then reload.
    async ensureSections(sections) {
      const missing = (sec) => !sec || this.sections().some((id) => sec[id] === null);
      if (!missing(sections)) return sections;
      await new Promise((r) => setTimeout(r, 400));
      const retry = await this.fetchSections().catch(() => null);
      if (!missing(retry)) return retry;
      window.location.reload();
      return null;
    },

    render(sections) {
      if (!sections) return;
      this.roots().forEach((root) => {
        const html = sections[root.dataset.section];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-sok-cart-root="${root.dataset.mode}"]`) || doc.querySelector(root.tagName.toLowerCase());
        const target = root.querySelector(`[data-sok-cart-root="${root.dataset.mode}"]`) || root;
        if (fresh) target.innerHTML = fresh.innerHTML;
        root.onRendered?.();
      });
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        bubble.innerHTML = doc.querySelector('.shopify-section')?.innerHTML ?? doc.body.innerHTML;
      }
      SokCart.syncUndo();
    },

    publish(cart) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sok-cart', cartData: cart });
      }
    },

    async run(trigger, task) {
      const root = trigger?.closest('sok-cart, cart-drawer');
      root?.classList.add('is-busy');
      const leaving = trigger?.matches('[data-sok-cart-remove]') ? trigger.closest('[data-leave]') : null;
      if (leaving) leaving.classList.add('is-leaving');
      try {
        const started = Date.now();
        const data = await task();
        data.sections = await this.ensureSections(data.sections);
        // Let the leave animation finish before the new markup lands.
        if (leaving) await new Promise((r) => setTimeout(r, Math.max(0, 420 - (Date.now() - started))));
        this.render(data.sections);
        this.publish(data);
        return data;
      } catch (error) {
        leaving?.classList.remove('is-leaving');
        console.error(error);
        root?.querySelector('[data-sok-cart-error]')?.removeAttribute('hidden');
      } finally {
        root?.classList.remove('is-busy');
      }
    },

    remove(trigger) {
      const keys = trigger.dataset.sokCartRemove.split(',').filter(Boolean);
      if (trigger.dataset.restore) {
        try {
          sessionStorage.setItem(UNDO_KEY, trigger.dataset.restore);
        } catch (e) {}
      }
      const updates = Object.fromEntries(keys.map((key) => [key, 0]));
      return this.run(trigger, () => this.post('/cart/update', { updates }));
    },

    quantity(trigger) {
      const quantity = Math.max(0, Number(trigger.dataset.qty));
      return this.run(trigger, () => this.post('/cart/change', { id: trigger.dataset.sokCartQty, quantity }));
    },

    // Swap a sauna to the other installation variant, keeping its bundle properties.
    swap(trigger) {
      // Slide the pill straight away; the cart catches up behind it.
      const toggle = trigger.closest('.sok-cart-install__toggle');
      if (toggle) {
        toggle.classList.toggle('is-self');
        toggle.querySelectorAll('[role="radio"]').forEach((b) => b.setAttribute('aria-checked', String(b === trigger)));
      }
      return this.run(trigger, async () => {
        const properties = toProps(JSON.parse(trigger.dataset.properties || '{}'));
        // Add the new variant before removing the old one, so a failed add never empties the bundle.
        await this.post('/cart/add', {
          items: [{ id: Number(trigger.dataset.variant), quantity: Number(trigger.dataset.quantity) || 1, properties }],
        });
        return this.post('/cart/update', { updates: { [trigger.dataset.sokCartSwap]: 0 } });
      });
    },

    // Installation as a product: add its line to the sauna's bundle, or remove it (data-remove holds its key).
    install(trigger) {
      const toggle = trigger.closest('.sok-cart-install__toggle');
      if (toggle) {
        toggle.classList.toggle('is-self');
        toggle.querySelectorAll('[role="radio"]').forEach((b) => b.setAttribute('aria-checked', String(b === trigger)));
      }
      if (trigger.dataset.remove) {
        return this.run(trigger, () => this.post('/cart/update', { updates: { [trigger.dataset.remove]: 0 } }));
      }
      return this.run(trigger, () => this.post('/cart/add', { items: normalise(JSON.parse(trigger.dataset.sokCartInstall)) }));
    },

    add(trigger, items) {
      return this.run(trigger, async () => {
        return this.post('/cart/add', { items: normalise(items) });
      });
    },

    undo(trigger) {
      let items = [];
      try {
        items = JSON.parse(sessionStorage.getItem(UNDO_KEY) || '[]');
        sessionStorage.removeItem(UNDO_KEY);
      } catch (e) {}
      if (items.length) return this.add(trigger, items);
    },

    syncUndo() {
      let has = false;
      try {
        has = Boolean(sessionStorage.getItem(UNDO_KEY));
      } catch (e) {}
      document.querySelectorAll('[data-sok-cart-undo]').forEach((button) => (button.hidden = !has));
    },

    async applyDiscount(form) {
      const box = form.closest('[data-sok-discount]');
      const input = form.querySelector('input');
      const code = input.value.trim();
      const showError = (scope, message) => {
        const f = scope.querySelector('[data-sok-discount-form]');
        if (!f) return;
        f.hidden = false;
        const error = f.querySelector('.sok-discount__error');
        error.querySelector('[data-message]').textContent = message;
        error.hidden = false;
        const fieldInput = f.querySelector('input');
        fieldInput.setAttribute('aria-invalid', 'true');
        fieldInput.value = code;
        f.classList.remove('is-shaking');
        void f.offsetWidth;
        f.classList.add('is-shaking');
      };
      if (!code) return showError(box, box.dataset.empty);

      const label = form.querySelector('[data-label]');
      const button = form.querySelector('button');
      button.disabled = true;
      label.innerHTML =
        '<svg class="sok-spinner" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" stroke-opacity=".25" stroke-width="2"/><path d="M9 2a7 7 0 0 1 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

      const existing = (box.dataset.codes || '').split(',').filter(Boolean);
      const root = form.closest('sok-cart, cart-drawer');
      try {
        const data = await this.post('/cart/update', { discount: [...existing, code].join(',') });
        const match = (data.discount_codes || []).find((d) => d.code.toUpperCase() === code.toUpperCase());
        if (match && match.applicable) {
          this.render(await this.ensureSections(data.sections));
          this.publish(data);
          return;
        }
        // Invalid or not applicable: take it back off so it doesn't linger in the cart.
        const cleaned = await this.post('/cart/update', { discount: existing.join(',') });
        this.render(await this.ensureSections(cleaned.sections));
        const scope = root?.querySelector('[data-sok-discount]') || box;
        showError(scope, box.dataset.invalid);
      } catch (e) {
        button.disabled = false;
        label.textContent = button.dataset.idle || 'Apply';
        showError(box, box.dataset.invalid);
      }
    },

    removeDiscount(trigger) {
      const box = trigger.closest('[data-sok-discount]');
      const remaining = (box.dataset.codes || '').split(',').filter((c) => c && c !== trigger.dataset.sokDiscountRemove);
      return this.run(trigger, () => this.post('/cart/update', { discount: remaining.join(',') }));
    },
  };

  window.SokCart = SokCart;

  document.addEventListener('click', (event) => {
    const t = event.target;
    let el;
    if ((el = t.closest('[data-sok-cart-remove]'))) SokCart.remove(el);
    else if ((el = t.closest('[data-sok-cart-qty]'))) SokCart.quantity(el);
    else if ((el = t.closest('[data-sok-cart-swap]'))) SokCart.swap(el);
    else if ((el = t.closest('[data-sok-cart-install]'))) SokCart.install(el);
    else if ((el = t.closest('[data-sok-cart-add]'))) {
      el.disabled = true;
      SokCart.add(el, JSON.parse(el.dataset.sokCartAdd));
    } else if ((el = t.closest('[data-sok-cart-undo]'))) SokCart.undo(el);
    else if ((el = t.closest('[data-sok-discount-remove]'))) SokCart.removeDiscount(el);
  });

  document.addEventListener('submit', (event) => {
    const discountForm = event.target.closest('[data-sok-discount-form]');
    if (discountForm) {
      event.preventDefault();
      SokCart.applyDiscount(discountForm);
      return;
    }
    const checkout = event.target.closest('[data-sok-checkout]');
    if (checkout) {
      const label = checkout.querySelector('[data-label]');
      if (label) {
        label.innerHTML =
          '<svg class="sok-spinner" width="22" height="22" viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" stroke-opacity=".18" stroke-width="2"/><path d="M9 2a7 7 0 0 1 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      }
    }
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('.sok-discount__input');
    if (!input) return;
    input.removeAttribute('aria-invalid');
    const error = input.closest('form')?.querySelector('.sok-discount__error');
    if (error) error.hidden = true;
  });

  // Keep in step with carts changed elsewhere (product forms, other tabs of the same component).
  document.addEventListener('DOMContentLoaded', () => {
    SokCart.syncUndo();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        if (event.source === 'sok-cart') return;
        SokCart.render(await SokCart.fetchSections());
      });
    }
  });
}

if (!customElements.get('sok-cart')) {
  customElements.define('sok-cart', class extends HTMLElement {});
}

if (!customElements.get('cart-drawer')) {
  class SokCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[role="dialog"]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-sok-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.addEventListener('click', (event) => {
        const link = event.target.closest('#cart-icon-bubble');
        if (link && !document.body.classList.contains('template-cart')) {
          event.preventDefault();
          this.open(link);
        }
      });
    }

    open(opener) {
      if (opener) this.opener = opener;
      clearTimeout(this.hideTimer);
      this.hidden = false;
      document.body.classList.add('sok-lock');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      this.panel.focus();
      if (typeof trapFocus === 'function') trapFocus(this.panel);
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('sok-lock');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
      this.hideTimer = setTimeout(() => (this.hidden = true), 550);
    }

    // Called by product forms after /cart/add.js (Dawn's contract).
    renderContents(parsedState) {
      window.SokCart.render(parsedState.sections);
      this.open();
    }

    getSectionsToRender() {
      return [{ id: this.dataset.section }, { id: 'cart-icon-bubble' }];
    }

    setActiveElement(element) {
      this.opener = element;
    }
  }

  customElements.define('cart-drawer', SokCartDrawer);
}
